import * as THREE from 'three';

export class TerrainFeatures {
    constructor(scene, noiseGenerator, materials) {
        this.scene = scene;
        this.noise = noiseGenerator;
        this.materials = materials;
    }
    
    generateFeaturesAt(x, y, z, chunk, biome) {
        // Check each possible feature for the biome
        Object.entries(biome.features || {}).forEach(([featureType, config]) => {
            const featureNoise = this.noise.getFeatureNoiseAt(x, z);
            
            if (featureNoise < config.chance) {
                this.generateFeature(featureType, x, y, z, chunk, config);
            }
        });
    }
    
    generateFeature(type, x, y, z, chunk, config) {
        switch (type) {
            case 'tree':
                this.generateTree(x, y, z, chunk, config);
                break;
            case 'cactus':
                this.generateCactus(x, y, z, chunk, config);
                break;
            case 'tallGrass':
                this.generateTallGrass(x, y, z, chunk);
                break;
        }
    }
    
    generateTree(x, y, z, chunk, config) {
        // Calculate local chunk coordinates
        const localX = x - chunk.x * 16;
        const localZ = z - chunk.z * 16;
        
        // Determine tree height
        const height = Math.floor(
            config.height.min + 
            Math.random() * (config.height.max - config.height.min)
        );
        
        // Generate trunk
        for (let h = 1; h <= height; h++) {
            this.placeFeatureBlock(chunk, localX, y + h, localZ, 'wood');
        }
        
        // Generate leaves
        const leafStart = Math.max(height - 3, 1);
        const leafSize = 2;
        
        for (let lx = -leafSize; lx <= leafSize; lx++) {
            for (let ly = 0; ly <= leafSize + 1; ly++) {
                for (let lz = -leafSize; lz <= leafSize; lz++) {
                    // Skip trunk positions
                    if (lx === 0 && lz === 0 && ly < leafSize + 1) continue;
                    
                    // Create a rounded leaf shape
                    const distance = Math.sqrt(lx*lx + ly*ly + lz*lz);
                    if (distance <= leafSize + 0.5) {
                        this.placeFeatureBlock(
                            chunk, 
                            localX + lx, 
                            y + leafStart + ly, 
                            localZ + lz, 
                            'leaves'
                        );
                    }
                }
            }
        }
    }
    
    generateCactus(x, y, z, chunk, config) {
        const localX = x - chunk.x * 16;
        const localZ = z - chunk.z * 16;
        
        const height = Math.floor(
            config.height.min + 
            Math.random() * (config.height.max - config.height.min)
        );
        
        for (let h = 1; h <= height; h++) {
            this.placeFeatureBlock(chunk, localX, y + h, localZ, 'cactus');
        }
    }
    
    generateTallGrass(x, y, z, chunk) {
        const localX = x - chunk.x * 16;
        const localZ = z - chunk.z * 16;
        
        this.placeFeatureBlock(chunk, localX, y + 1, localZ, 'tallGrass');
    }
    
    placeFeatureBlock(chunk, x, y, z, type) {
        // Make sure coordinates are within chunk bounds
        if (x < 0 || x >= 16 || z < 0 || z >= 16) return;
        
        const blockKey = `${x},${y},${z}`;
        if (chunk.blocks[blockKey]) return;
        
        // Use shared materials from TerrainMaterials
        const geometry = new THREE.BoxGeometry(1, 1, 1);
        const material = this.materials[type] || this.materials.dirt;
        const mesh = new THREE.Mesh(geometry, material);
        
        mesh.position.set(
            chunk.x * 16 + x,
            y,
            chunk.z * 16 + z
        );
        
        chunk.mesh.add(mesh);
        chunk.blocks[blockKey] = {
            type,
            mesh
        };
    }
}
